import { request } from '../http';

export type BillingPlan = {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  priceCents: number;
  currency: string;
  billingInterval: string;
  maxSearches: number;
  maxSourcesPerSearch?: number | null;
  maxNotificationRecipients?: number | null;
  maxNotificationsPerDay?: number | null;
  minScrapeIntervalMinutes?: number | null;
  historyDays?: number | null;
  isActive: boolean;
  configJson?: Record<string, unknown>;
  subscriptionCount?: number;
};

export type BillingEntitlement = {
  id: string;
  organizationId: string;
  organizationName?: string | null;
  organizationSlug?: string | null;
  planSlug?: string | null;
  planName?: string | null;
  status: string;
  source: string;
  startsAt?: string | null;
  expiresAt?: string | null;
  createdAt?: string | null;
};

export type OrgUsageSummary = {
  organizationId: string;
  activeSearches: number;
  recipients: number;
  subscriptions: number;
  notificationsSentToday: number;
  planSlug?: string | null;
  entitlementStatus?: string | null;
  entitlementSource?: string | null;
  unlimited?: boolean;
  subscriptionActive: boolean;
  expiresAt?: string | null;
  limits: {
    maxSearches?: number | null;
    maxSourcesPerSearch?: number | null;
    maxNotificationRecipients?: number | null;
    maxNotificationsPerDay?: number | null;
    minScrapeIntervalMinutes?: number | null;
  };
  usage: {
    activeSearches: number;
    recipients: number;
    notificationsSentToday: number;
  };
};

export const billingAdminService = {
  listPlans(includeInactive = false) {
    const q = includeInactive ? '?include_inactive=true' : '';
    return request<{ items: BillingPlan[] }>(`/billing/plans${q}`);
  },

  createPlan(body: Partial<BillingPlan> & { name: string; slug: string; maxSearches: number }) {
    return request<BillingPlan>('/billing/plans', { method: 'POST', body: JSON.stringify(body) });
  },

  updatePlan(id: string, body: Partial<BillingPlan>) {
    return request<BillingPlan>(`/billing/plans/${id}`, { method: 'PATCH', body: JSON.stringify(body) });
  },

  deletePlan(id: string) {
    return request<void>(`/billing/plans/${id}`, { method: 'DELETE' });
  },

  duplicatePlan(id: string) {
    return request<BillingPlan>(`/billing/plans/${id}/duplicate`, { method: 'POST' });
  },

  activatePlan(id: string) {
    return request<BillingPlan>(`/billing/plans/${id}/activate`, { method: 'POST' });
  },

  deactivatePlan(id: string) {
    return request<BillingPlan>(`/billing/plans/${id}/deactivate`, { method: 'POST' });
  },

  listEntitlements(params?: { organizationId?: string; activeOnly?: boolean }) {
    const search = new URLSearchParams();
    if (params?.organizationId) search.set('organization_id', params.organizationId);
    if (params?.activeOnly) search.set('active_only', 'true');
    const q = search.toString();
    return request<{ items: BillingEntitlement[] }>(`/billing/entitlements${q ? `?${q}` : ''}`);
  },

  grantEntitlement(body: {
    organizationId: string;
    planSlug: string;
    expiresAt?: string;
    reason?: string;
  }) {
    return request<BillingEntitlement>('/billing/entitlements/grant', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  },

  revokeEntitlement(id: string, reason?: string) {
    const q = reason ? `?reason=${encodeURIComponent(reason)}` : '';
    return request<{ entitlementId: string; organizationId: string; status: string }>(
      `/billing/entitlements/${id}/revoke${q}`,
      { method: 'POST' },
    );
  },

  usage(organizationId?: string) {
    const q = organizationId ? `?organization_id=${encodeURIComponent(organizationId)}` : '';
    return request<OrgUsageSummary>(`/billing/usage${q}`);
  },
};
